/**
 * vore-byb.js — Build Your Bundle da VORE.
 *
 * Porte do JS inline da secao `build_your_bundle_wR7tRP` de usevore.com
 * (INVENTORY.md 4.8, analysis/pages-core.md 6, ~37.9 KB de secao).
 * A mecanica de preco e identica a do site; o que mudou esta marcado
 * `MELHORIA:` e registrado em VORE-DIVERGENCIAS.md, etapa `byb`.
 *
 * Regra de preco original, preservada:
 *   compareTotal  = soma de data-compare-price de cada unidade
 *   sellingTotal  = soma do preco unitario do modo atual (one-time ou assinatura)
 *   se units.length === pack.qty e pack.saveQty > 0:
 *       ordena por preco e zera as `saveQty` mais baratas
 *   totalSavings  = max(compareTotal - finalTotal, 0)
 *   savePercent   = round(totalSavings / compareTotal * 100)
 *
 * MELHORIAS:
 *   1. Estado persistido em localStorage + parametro de URL (o site perde tudo
 *      no reload).
 *   2. `compareMode: "coherent"` recalcula o riscado no modo assinatura contra
 *      o preco one-time em vez do compare-at. O default continua sendo "site".
 *   3. Slots, steppers e CTA sao <button> reais, operaveis por teclado, e o
 *      estado e anunciado num container aria-live.
 *   4. A barra fixa do mobile publica a propria altura em --byb-bar-height
 *      para o conteudo nao ficar embaixo dela.
 *
 * ALERTA COMERCIAL (igual ao site, nao corrigido aqui): este widget NAO aplica
 * desconto no carrinho. Ele adiciona as N unidades a preco cheio e apenas
 * EXIBE o total com as gratis abatidas. O desconto real depende de um desconto
 * automatico da Shopify. Ver vore-import/IMPORT-README.md secao 7.
 */

import { Component } from "@theme/component";
import { CartAddEvent, CartErrorEvent, CartGroupedSections, ThemeEvents } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

const STORAGE_PREFIX = "rst:byb:";
const STORAGE_VERSION = 1;
const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias
const MOBILE_BREAKPOINT = 991;
const ERROR_TIMEOUT = 6000;

const ICON_MINUS =
  '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M5 10h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
const ICON_PLUS =
  '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M10 5v10M5 10h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

/**
 * @typedef {Object} BybUnit
 * @property {string} id
 * @property {number} price
 * @property {number} comparePrice
 * @property {number} subPrice
 * @property {number} subComparePrice
 * @property {string} sellingPlanId
 * @property {string} image
 * @property {string} title
 * @property {string} slotTitle
 * @property {boolean} available
 */

class RstBuildYourBundle extends Component {
  /** @type {any} */
  #config = {};
  /** @type {BybUnit[]} */
  #units = [];
  /** @type {string} */
  #packKey = "";
  /** @type {string} */
  #purchase = "one_time";
  /** @type {AbortController} */
  #abort = new AbortController();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #errorTimeout;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {boolean} */
  #ready = false;

  connectedCallback() {
    super.connectedCallback();

    // Reconexao (o editor de tema move a secao no DOM): derruba os listeners
    // do ciclo anterior antes de registrar os novos.
    this.#abort.abort();
    this.#ready = false;

    const configEl = this.querySelector("[data-byb-config]");
    if (!configEl) return;

    try {
      this.#config = JSON.parse(configEl.textContent || "{}");
    } catch (error) {
      console.error("vore-byb: config invalida", error);
      return;
    }

    if (!this.#config.packs || !Object.keys(this.#config.packs).length) return;

    this.#abort = new AbortController();
    this.#packKey = this.#config.defaultPack || Object.keys(this.#config.packs)[0];
    this.#purchase = this.#config.defaultPurchase === "subscribe" ? "subscribe" : "one_time";

    this.#restoreState();
    this.#bind();
    this.#render();
    this.#ready = true;
    this.#measureBar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#resizeObserver?.disconnect();
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
  }

  /* ----------------------------------------------------------------------
     Leitura do DOM
     ---------------------------------------------------------------------- */

  /** @returns {HTMLElement[]} */
  get #cards() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll(".byb-item")]);
  }

  get #grid() {
    return this.querySelector(".byb-grid");
  }

  get #cta() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector("[data-byb-cta]"));
  }

  /**
   * @param {HTMLElement} card
   * @returns {BybUnit}
   */
  #cardData(card) {
    const d = card.dataset;
    const price = Number(d.price || 0);
    return {
      id: String(d.variant || ""),
      price,
      comparePrice: Number(d.comparePrice || price),
      subPrice: Number(d.subPrice || price),
      subComparePrice: Number(d.subComparePrice || d.comparePrice || price),
      sellingPlanId: d.sellingPlan || "",
      image: d.image || "",
      title: d.title || "",
      slotTitle: d.slotTitle || d.title || "",
      available: d.available !== "false",
    };
  }

  /**
   * @param {string} id
   * @returns {BybUnit | null}
   */
  #cardDataById(id) {
    const card = this.#cards.find((item) => String(item.dataset.variant) === String(id));
    return card ? this.#cardData(card) : null;
  }

  /* ----------------------------------------------------------------------
     Packs
     ---------------------------------------------------------------------- */

  /**
   * @param {string} [key]
   * @returns {{key: string, qty: number, saveQty: number, subtextMode: string, customSubtext: string, dynamicSuffix: string}}
   */
  #pack(key) {
    const packKey = key || this.#packKey;
    const raw = this.#config.packs[packKey] || {};
    const qty = Math.max(1, Number(raw.qty || 1));
    return {
      key: packKey,
      qty,
      saveQty: Math.max(0, Math.min(Number(raw.saveQty || 0), qty)),
      subtextMode: raw.subtextMode || "dynamic",
      customSubtext: raw.customSubtext || "",
      dynamicSuffix: raw.dynamicSuffix || "",
    };
  }

  /* ----------------------------------------------------------------------
     Precos
     ---------------------------------------------------------------------- */

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const locale = this.#config.locale || document.documentElement.lang || "pt-BR";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: this.#config.currencyCode || "BRL",
      }).format((cents || 0) / 100);
    } catch {
      return `${((cents || 0) / 100).toFixed(2)}`;
    }
  }

  /**
   * @param {BybUnit} unit
   * @param {string} [purchase]
   * @returns {number}
   */
  #unitPrice(unit, purchase) {
    const mode = purchase || this.#purchase;
    if (mode === "subscribe" && unit.subPrice) return unit.subPrice;
    return unit.price;
  }

  /**
   * Riscado por unidade.
   * `site`     — sempre o compare-at, inclusive na assinatura (gera Save 49% / 57%).
   * `coherent` — na assinatura compara com o preco one-time (data-sub-compare-price).
   * @param {BybUnit} unit
   * @returns {number}
   */
  #compareUnitPrice(unit) {
    if (this.#config.compareMode === "coherent" && this.#purchase === "subscribe") {
      return unit.subComparePrice || unit.comparePrice || unit.price;
    }
    return unit.comparePrice || unit.price;
  }

  #totals() {
    const pack = this.#pack();
    const compareTotal = this.#units.reduce((sum, unit) => sum + this.#compareUnitPrice(unit), 0);
    const sellingTotal = this.#units.reduce((sum, unit) => sum + this.#unitPrice(unit), 0);

    let finalTotal = sellingTotal;
    const packComplete = this.#units.length === pack.qty;
    const applies = pack.saveQty > 0 && (this.#config.requireFullPack === false || packComplete);

    if (applies && this.#units.length) {
      const sorted = [...this.#units].sort((a, b) => this.#unitPrice(a) - this.#unitPrice(b));
      if (this.#config.freeUnitsRule === "most_expensive") sorted.reverse();

      const freeCount = Math.min(pack.saveQty, this.#units.length);
      const discount = sorted.slice(0, freeCount).reduce((sum, unit) => sum + this.#unitPrice(unit), 0);
      finalTotal = sellingTotal - discount;
    }

    const totalSavings = Math.max(compareTotal - finalTotal, 0);
    const savePercent = compareTotal > 0 ? Math.round((totalSavings / compareTotal) * 100) : 0;

    return { compareTotal, sellingTotal, finalTotal, totalSavings, savePercent };
  }

  /**
   * Media do preco unitario: das unidades escolhidas ou, se nao ha nenhuma,
   * de todos os cards elegiveis (igual ao site).
   * @returns {number}
   */
  #averageUnitPrice() {
    const reference = this.#units.length ? this.#units : this.#cards.map((card) => this.#cardData(card));
    if (!reference.length) return 0;
    const total = reference.reduce((sum, unit) => sum + this.#unitPrice(unit), 0);
    return Math.round(total / reference.length);
  }

  /**
   * @param {string} packKey
   * @returns {string}
   */
  #packSubtext(packKey) {
    const pack = this.#pack(packKey);
    if (pack.subtextMode === "custom") return pack.customSubtext;

    const avg = this.#averageUnitPrice();
    if (!avg || !pack.qty) return pack.customSubtext;

    const paidQty = Math.max(pack.qty - pack.saveQty, 0);
    const perUnit = Math.round((avg * paidQty) / pack.qty);
    return `${this.#money(perUnit)}${pack.dynamicSuffix ? ` ${pack.dynamicSuffix}` : ""}`;
  }

  /* ----------------------------------------------------------------------
     Templates de texto
     ---------------------------------------------------------------------- */

  /**
   * @param {string} template
   * @param {Record<string, string | number>} vars
   * @returns {string}
   */
  #fill(template, vars) {
    return String(template || "").replace(/\[(\w+)\]/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match
    );
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  #unitWord(count) {
    return count === 1 ? this.#config.unitSingular : this.#config.unitPlural;
  }

  /* ----------------------------------------------------------------------
     Persistencia — MELHORIA 1
     ---------------------------------------------------------------------- */

  get #storageKey() {
    return `${STORAGE_PREFIX}${this.dataset.sectionId || "default"}`;
  }

  /**
   * Aplica pack / modo / ids validando contra os cards realmente presentes.
   * @param {{pack?: string, purchase?: string, units?: (string|number)[]}} state
   * @returns {boolean} true se restaurou pelo menos algo
   */
  #applyState(state) {
    if (!state) return false;
    let touched = false;

    if (state.pack && this.#config.packs[state.pack]) {
      this.#packKey = state.pack;
      touched = true;
    }
    if (state.purchase === "subscribe" || state.purchase === "one_time") {
      // So aceita assinatura se o toggle existir nesta secao.
      if (state.purchase === "one_time" || this.querySelector('[data-purchase-type="subscribe"]')) {
        this.#purchase = state.purchase;
        touched = true;
      }
    }

    if (Array.isArray(state.units) && state.units.length) {
      const limit = this.#pack().qty;
      /** @type {BybUnit[]} */
      const restored = [];
      for (const id of state.units) {
        if (restored.length >= limit) break;
        const unit = this.#cardDataById(id);
        if (unit && unit.available) restored.push(unit);
      }
      this.#units = restored;
      touched = touched || restored.length > 0;
    }

    return touched;
  }

  #restoreState() {
    if (this.#config.persist === false) return;

    // 1) parametro de URL tem prioridade (link compartilhado)
    const param = this.#config.urlParam;
    if (param) {
      try {
        const raw = new URL(window.location.href).searchParams.get(param);
        if (raw) {
          const [pack, purchase, ids] = raw.split(".");
          if (this.#applyState({ pack, purchase, units: ids ? ids.split("-") : [] })) return;
        }
      } catch {
        /* URL malformada: cai para o localStorage */
      }
    }

    // 2) localStorage
    try {
      const raw = window.localStorage.getItem(this.#storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.v !== STORAGE_VERSION) return;
      if (!saved.t || Date.now() - saved.t > STORAGE_TTL_MS) {
        window.localStorage.removeItem(this.#storageKey);
        return;
      }
      this.#applyState(saved);
    } catch {
      /* modo privado / storage cheio: segue sem estado salvo */
    }
  }

  #saveState() {
    if (this.#config.persist === false) return;

    const ids = this.#units.map((unit) => unit.id);

    try {
      if (!ids.length) {
        window.localStorage.removeItem(this.#storageKey);
      } else {
        window.localStorage.setItem(
          this.#storageKey,
          JSON.stringify({
            v: STORAGE_VERSION,
            pack: this.#packKey,
            purchase: this.#purchase,
            units: ids,
            t: Date.now(),
          })
        );
      }
    } catch {
      /* storage indisponivel: o widget continua funcionando na sessao */
    }

    const param = this.#config.urlParam;
    if (!param || !window.history?.replaceState) return;

    try {
      const url = new URL(window.location.href);
      if (ids.length) {
        url.searchParams.set(param, `${this.#packKey}.${this.#purchase}.${ids.join("-")}`);
      } else {
        url.searchParams.delete(param);
      }
      window.history.replaceState(window.history.state, "", url.toString());
    } catch {
      /* nada a fazer: a URL e so um atalho de compartilhamento */
    }
  }

  /* ----------------------------------------------------------------------
     Eventos
     ---------------------------------------------------------------------- */

  #bind() {
    const signal = this.#abort.signal;

    this.querySelectorAll(".byb-pack").forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          const key = /** @type {HTMLElement} */ (button).dataset.packKey;
          if (!key || !this.#config.packs[key]) return;
          this.#packKey = key;
          const limit = this.#pack().qty;
          if (this.#units.length > limit) this.#units = this.#units.slice(0, limit);
          this.#render({ announce: true });
        },
        { signal }
      );
    });

    this.querySelectorAll(".byb-purchase-option").forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          const type = /** @type {HTMLElement} */ (button).dataset.purchaseType;
          if (type !== "one_time" && type !== "subscribe") return;
          this.#purchase = type;
          this.#render({ announce: true });
        },
        { signal }
      );
    });

    // Delegacao unica na grade de cards: cobre ADD, + e -.
    this.querySelector(".byb-items")?.addEventListener(
      "click",
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const card = /** @type {HTMLElement | null} */ (target.closest(".byb-item"));
        if (!card) return;

        const action = /** @type {HTMLElement | null} */ (target.closest("[data-byb-action]"));
        if (!action) return;

        const unit = this.#cardData(card);
        const limit = this.#pack().qty;
        const type = action.dataset.bybAction;

        if (type === "add" || type === "increase") {
          if (!unit.available || this.#units.length >= limit) return;
          this.#units.push(unit);
          this.#render({ announce: true, focus: `card:${unit.id}` });
          return;
        }

        if (type === "decrease") {
          const index = this.#units.findIndex((item) => item.id === unit.id);
          if (index === -1) return;
          this.#units.splice(index, 1);
          this.#render({ announce: true, focus: `card:${unit.id}` });
        }
      },
      { signal }
    );

    this.#grid?.addEventListener(
      "click",
      (event) => {
        const button = /** @type {HTMLElement | null} */ (
          /** @type {HTMLElement} */ (event.target).closest(".byb-remove")
        );
        if (!button) return;
        const index = Number(button.dataset.index);
        if (Number.isNaN(index) || !this.#units[index]) return;
        this.#units.splice(index, 1);
        this.#render({ announce: true, focus: "grid" });
      },
      { signal }
    );

    this.querySelector(".toggle-down")?.addEventListener(
      "click",
      (event) => {
        const button = /** @type {HTMLElement} */ (event.currentTarget);
        const open = button.getAttribute("aria-expanded") !== "true";
        this.#setPanelOpen(open);
      },
      { signal }
    );

    this.#cta?.addEventListener("click", () => this.#addToCart(), { signal });

    window.addEventListener("resize", this.#handleResize, { signal, passive: true });

    if ("ResizeObserver" in window) {
      const panel = this.querySelector(".byb-right");
      if (panel) {
        this.#resizeObserver = new ResizeObserver(() => this.#measureBar());
        this.#resizeObserver.observe(panel);
      }
    }
  }

  #handleResize = () => {
    this.#syncGridHeight();
    this.#measureBar();
  };

  /* ----------------------------------------------------------------------
     Acordeao mobile
     ---------------------------------------------------------------------- */

  /** @param {boolean} open */
  #setPanelOpen(open) {
    const toggle = this.querySelector(".toggle-down");
    if (!toggle || !this.#grid) return;
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    this.#grid.classList.toggle("is-open", open);
    this.#syncGridHeight();
  }

  #syncGridHeight() {
    const grid = /** @type {HTMLElement | null} */ (this.#grid);
    if (!grid) return;

    if (window.innerWidth > MOBILE_BREAKPOINT) {
      grid.style.height = "";
      return;
    }

    if (!grid.classList.contains("is-open")) {
      grid.style.height = "0px";
      return;
    }

    const clone = /** @type {HTMLElement} */ (grid.cloneNode(true));
    clone.style.height = "auto";
    clone.style.position = "absolute";
    clone.style.visibility = "hidden";
    clone.style.pointerEvents = "none";
    clone.style.width = `${grid.offsetWidth}px`;
    clone.classList.add("is-open");
    grid.parentElement?.appendChild(clone);
    const height = clone.scrollHeight;
    clone.remove();
    grid.style.height = `${height}px`;
  }

  /** MELHORIA 4: a barra fixa publica a propria altura. */
  #measureBar() {
    const panel = /** @type {HTMLElement | null} */ (this.querySelector(".byb-right"));
    if (!panel) return;
    if (window.innerWidth > MOBILE_BREAKPOINT) {
      this.style.removeProperty("--byb-bar-height");
      return;
    }
    this.style.setProperty("--byb-bar-height", `${Math.round(panel.offsetHeight)}px`);
  }

  /* ----------------------------------------------------------------------
     Render
     ---------------------------------------------------------------------- */

  /**
   * @param {{announce?: boolean, focus?: string}} [options]
   */
  #render(options = {}) {
    const pack = this.#pack();
    const limit = pack.qty;
    const totals = this.#totals();

    this.#renderSlots(limit);
    this.#renderPacks();
    this.#renderPurchase();
    this.#renderPrice(totals);
    this.#renderCta(limit);
    this.#renderCards(limit);

    if (this.#units.length === limit && limit > 0) this.#openIfComplete();
    this.#syncGridHeight();
    this.#measureBar();
    this.#saveState();

    if (options.announce && this.#ready) this.#announce(limit, totals);
    if (options.focus) this.#restoreFocus(options.focus);
  }

  /** @param {number} limit */
  #renderSlots(limit) {
    const grid = this.#grid;
    if (!grid) return;

    const parts = [];

    this.#units.forEach((unit, index) => {
      const label = this.#fill(this.#config.removeLabel, { title: unit.slotTitle });
      const image = unit.image
        ? `<img src="${escapeAttr(unit.image)}" alt="" width="200" height="200" loading="lazy">`
        : "";
      parts.push(
        `<li class="byb-slot-wrap">` +
          `<div class="byb-slot">` +
          `<button type="button" class="byb-remove" data-index="${index}" aria-label="${escapeAttr(label)}">` +
          `<span aria-hidden="true">&times;</span>` +
          `</button>` +
          image +
          `</div>` +
          `<p class="byb-slot-title">${escapeHtml(unit.slotTitle)}</p>` +
          `</li>`
      );
    });

    for (let i = this.#units.length; i < limit; i += 1) {
      parts.push(
        `<li class="byb-slot-wrap">` +
          `<div class="byb-slot byb-slot--empty">` +
          `<span class="vore-visually-hidden">${escapeHtml(
            this.#fill(this.#config.emptySlotLabel, { index: i + 1, total: limit })
          )}</span>` +
          `</div>` +
          `</li>`
      );
    }

    grid.innerHTML = parts.join("");
  }

  #renderPacks() {
    this.querySelectorAll(".byb-pack").forEach((button) => {
      const key = /** @type {HTMLElement} */ (button).dataset.packKey || "";
      const active = key === this.#packKey;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });

    Object.keys(this.#config.packs).forEach((key) => {
      const el = this.querySelector(`[data-pack-subtext="${key}"]`);
      if (el) el.textContent = this.#packSubtext(key);
    });
  }

  #renderPurchase() {
    this.querySelectorAll(".byb-purchase-option").forEach((button) => {
      const active = /** @type {HTMLElement} */ (button).dataset.purchaseType === this.#purchase;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  /** @param {{compareTotal: number, finalTotal: number, savePercent: number}} totals */
  #renderPrice(totals) {
    const row = /** @type {HTMLElement | null} */ (this.querySelector(".byb-price-row"));
    const current = this.querySelector("[data-byb-current-price]");
    const compare = /** @type {HTMLElement | null} */ (this.querySelector("[data-byb-compare-price]"));
    const badge = /** @type {HTMLElement | null} */ (this.querySelector("[data-byb-save-badge]"));

    if (!row) return;

    if (!this.#units.length) {
      row.hidden = true;
      if (current) current.textContent = "";
      if (compare) {
        compare.textContent = "";
        compare.hidden = true;
      }
      if (badge) {
        badge.textContent = "";
        badge.hidden = true;
      }
      return;
    }

    row.hidden = false;
    if (current) current.textContent = this.#money(totals.finalTotal);

    if (compare) {
      const show = totals.compareTotal > totals.finalTotal;
      compare.textContent = show ? this.#money(totals.compareTotal) : "";
      compare.hidden = !show;
    }

    if (badge) {
      const show = totals.savePercent > 0;
      badge.textContent = show ? this.#fill(this.#config.saveBadge, { percent: totals.savePercent }) : "";
      badge.hidden = !show;
    }
  }

  /** @param {number} limit */
  #renderCta(limit) {
    const cta = this.#cta;
    if (!cta) return;

    const remaining = limit - this.#units.length;

    if (remaining > 0) {
      cta.disabled = true;
      cta.textContent = this.#fill(this.#config.ctaIncomplete, {
        count: remaining,
        unit: this.#unitWord(remaining),
      });
    } else {
      cta.disabled = false;
      cta.textContent = this.#config.ctaComplete;
    }
  }

  /** @param {number} limit */
  #renderCards(limit) {
    const full = this.#units.length >= limit;

    this.#cards.forEach((card) => {
      const unit = this.#cardData(card);
      const qty = this.#units.filter((item) => item.id === unit.id).length;
      const blocked = full && qty === 0;

      card.classList.toggle("is-disabled", blocked);

      let control = card.querySelector(".byb-control");
      if (!control) {
        control = document.createElement("div");
        control.className = "byb-control";
        card.appendChild(control);
      }

      if (!unit.available) {
        control.innerHTML = `<button type="button" class="byb-atc" data-byb-action="add" disabled>${escapeHtml(
          this.#config.soldOutLabel
        )}</button>`;
        return;
      }

      if (qty === 0) {
        const label = this.#fill(this.#config.addAriaLabel, { title: unit.title });
        control.innerHTML =
          `<button type="button" class="byb-atc" data-byb-action="add" aria-label="${escapeAttr(label)}"` +
          `${blocked ? " disabled" : ""}>${escapeHtml(this.#config.addLabel)}</button>`;
        return;
      }

      const decLabel = this.#fill(this.#config.decreaseAriaLabel, { title: unit.title });
      const incLabel = this.#fill(this.#config.increaseAriaLabel, { title: unit.title });
      const countLabel = this.#fill(this.#config.countAriaLabel, { count: qty, title: unit.title });

      control.innerHTML =
        `<div class="byb-qty">` +
        `<button type="button" data-byb-action="decrease" aria-label="${escapeAttr(decLabel)}">${ICON_MINUS}</button>` +
        `<span class="byb-qty-num" aria-label="${escapeAttr(countLabel)}">${qty}</span>` +
        `<button type="button" data-byb-action="increase" aria-label="${escapeAttr(incLabel)}"` +
        `${full ? " disabled" : ""}>${ICON_PLUS}</button>` +
        `</div>`;
    });
  }

  #openIfComplete() {
    if (window.innerWidth > MOBILE_BREAKPOINT) return;
    const toggle = this.querySelector(".toggle-down");
    if (!toggle || toggle.getAttribute("aria-expanded") === "true") return;
    this.#setPanelOpen(true);
  }

  /* ----------------------------------------------------------------------
     Acessibilidade — MELHORIA 4
     ---------------------------------------------------------------------- */

  /**
   * @param {number} limit
   * @param {{finalTotal: number, savePercent: number}} totals
   */
  #announce(limit, totals) {
    const status = this.querySelector("[data-byb-status]");
    if (!status) return;

    const count = this.#units.length;
    let message = this.#fill(this.#config.statusTemplate, {
      count,
      total: limit,
      unit: this.#unitWord(limit),
    });

    if (count > 0 && this.#config.statusPriceTemplate) {
      message += ` ${this.#fill(this.#config.statusPriceTemplate, {
        price: this.#money(totals.finalTotal),
        percent: totals.savePercent,
      })}`;
    }

    status.textContent = message;
  }

  /**
   * Depois de reescrever o markup o foco cai no <body>. Devolve para o
   * controle equivalente ao que o usuario acabou de acionar.
   * @param {string} target
   */
  #restoreFocus(target) {
    if (target === "grid") {
      const next = /** @type {HTMLElement | null} */ (this.querySelector(".byb-remove")) ||
        /** @type {HTMLElement | null} */ (this.querySelector(".toggle-down"));
      next?.focus();
      return;
    }

    const [, id] = target.split(":");
    if (!id) return;
    const card = this.#cards.find((item) => String(item.dataset.variant) === id);
    const control = /** @type {HTMLElement | null} */ (
      card?.querySelector('[data-byb-action="increase"]:not([disabled])') ||
      card?.querySelector('[data-byb-action="add"]:not([disabled])') ||
      card?.querySelector("[data-byb-action]")
    );
    control?.focus();
  }

  /* ----------------------------------------------------------------------
     Carrinho
     ---------------------------------------------------------------------- */

  /** @returns {{id: number, quantity: number, selling_plan?: number}[]} */
  #cartItems() {
    /** @type {Record<string, {id: number, quantity: number, selling_plan?: number}>} */
    const map = {};

    this.#units.forEach((unit) => {
      const sellingPlanId = this.#purchase === "subscribe" ? unit.sellingPlanId : "";
      const key = `${unit.id}_${sellingPlanId || "one_time"}`;

      if (!map[key]) {
        map[key] = { id: Number(unit.id), quantity: 0 };
        if (sellingPlanId) map[key].selling_plan = Number(sellingPlanId);
      }

      map[key].quantity += 1;
    });

    return Object.values(map);
  }

  /** @returns {string[]} */
  #groupedSectionIds() {
    /** @type {string[]} */
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return [...new Set(sections)];
  }

  #addToCart() {
    const cta = this.#cta;
    if (!cta || cta.disabled || cta.classList.contains("is-loading")) return;

    const items = this.#cartItems();
    if (!items.length) return;

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    cta.classList.add("is-loading");
    cta.setAttribute("aria-busy", "true");
    this.#hideError();

    /** @type {{items: unknown[], sections?: string}} */
    const payload = { items };
    const sectionIds = this.#groupedSectionIds();
    if (sectionIds.length) payload.sections = sectionIds.join(",");

    const routes = window.KindTheme?.routes ?? {};
    const addUrl = routes.cart_add_url || `${window.Shopify?.routes?.root ?? "/"}cart/add.js`;
    const config = fetchConfig("javascript", { body: JSON.stringify(payload) });

    fetch(addUrl, {
      ...config,
      signal: this.#abort.signal,
      headers: { ...config.headers, "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((response) => {
        this.#stopLoading();

        if (response.status) {
          this.#showError(response.description || response.message);
          this.dispatchEvent(new CartErrorEvent(this.id || "vore-byb", response.message));
          document.dispatchEvent(
            new CustomEvent(ThemeEvents.productAjaxError, {
              detail: { errorMessage: response.description || response.message },
            })
          );
          return;
        }

        this.dispatchEvent(
          new CartAddEvent(response, this.id || "vore-byb", {
            source: "vore-build-your-bundle",
            itemCount,
            sections: response.sections,
          })
        );

        document.dispatchEvent(
          new CustomEvent(ThemeEvents.productAjaxAdded, { detail: { product: response } })
        );

        if (this.#config.clearAfterAdd) {
          this.#units = [];
          this.#render({ announce: true });
        }
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        this.#stopLoading();
        this.#showError(this.#config.errorText);
        console.error("vore-byb:", error);
      });
  }

  #stopLoading() {
    const cta = this.#cta;
    if (!cta) return;
    cta.classList.remove("is-loading");
    cta.removeAttribute("aria-busy");
  }

  /** @param {string} message */
  #showError(message) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector("[data-byb-error]"));
    if (!error) {
      console.error("vore-byb:", message);
      return;
    }
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    error.textContent = message;
    error.hidden = false;
    this.#errorTimeout = setTimeout(() => this.#hideError(), ERROR_TIMEOUT);
  }

  #hideError() {
    const error = /** @type {HTMLElement | null} */ (this.querySelector("[data-byb-error]"));
    if (!error) return;
    error.hidden = true;
    error.textContent = "";
  }
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeAttr(value) {
  return escapeHtml(value).replace(/"/g, "&quot;");
}

if (!customElements.get("vore-build-your-bundle")) {
  customElements.define("vore-build-your-bundle", RstBuildYourBundle);
}

export { RstBuildYourBundle };
